import React, { useState, useEffect } from 'react';
import { clientsApi } from '../../utils/api';
import { Modal, Button, Descriptions, Divider, Tag, Spin } from 'antd';
import { UserOutlined, PhoneOutlined, MailOutlined, TagOutlined, ClockCircleOutlined, ShopOutlined } from '@ant-design/icons';

/**
 * Composant modal pour afficher les détails d'un client
 * @param {Object} props - Les propriétés du composant
 * @param {string} props.clientId - L'ID du client à afficher
 * @param {boolean} props.visible - Si le modal est visible
 * @param {Function} props.onClose - Fonction à appeler pour fermer le modal
 */
const ClientDetails = ({ clientId, visible, onClose }) => {
  // États pour stocker les données du client, l'état de chargement et les erreurs
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Récupérer les détails du client lorsque le modal devient visible ou que l'ID change
  useEffect(() => {
    if (clientId && visible) {
      setLoading(true);
      setError(null);
      
      // Appeler l'API pour récupérer les détails du client
      clientsApi.getById(clientId)
        .then(response => {
          setClient(response.data);
          setLoading(false);
        })
        .catch(err => {
          console.error('Erreur lors de la récupération des détails du client:', err);
          setError('Impossible de charger les détails du client. Veuillez réessayer.');
          setLoading(false);
        });
    }
  }, [clientId, visible]);

  // Formater la date au format local français
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Déterminer le statut de la liste noire
  const getBlacklistStatus = () => {
    if (!client) return null;
    
    return client.is_blacklisted ? (
      <Tag color="red">Oui - {client.blacklist_reason || 'Aucune raison spécifiée'}</Tag>
    ) : (
      <Tag color="green">Non</Tag>
    );
  };

  // Déterminer l'affichage des points
  const getPointsDisplay = () => {
    if (!client || !client.customer_points || client.customer_points.length === 0) {
      return '0';
    }
    return client.customer_points[0]?.total_points || '0';
  };

  return (
    <Modal
      title={<span><UserOutlined /> Détails du Client</span>}
      open={visible}
      onCancel={onClose}
      footer={[
        <Button key="close" onClick={onClose}>
          Fermer
        </Button>
      ]}
      width={700}
    >
      {loading ? (
        <div style={{ textAlign: 'center', padding: '30px' }}>
          <Spin size="large" />
          <p style={{ marginTop: '10px' }}>Chargement des détails...</p>
        </div>
      ) : error ? (
        <div style={{ textAlign: 'center', color: 'red', padding: '20px' }}>
          {error}
        </div>
      ) : client ? (
        <div>
          <Descriptions bordered column={2}>
            <Descriptions.Item label="Nom" span={2}>
              <strong>{client.name || 'Non spécifié'}</strong>
            </Descriptions.Item>
            
            <Descriptions.Item label={<span><PhoneOutlined /> Téléphone</span>}>
              {client.phone || 'Non spécifié'}
            </Descriptions.Item>
            
            <Descriptions.Item label={<span><MailOutlined /> Email</span>}>
              {client.email || 'Non spécifié'}
            </Descriptions.Item>
            
            <Descriptions.Item label={<span><ShopOutlined /> Source</span>}>
              {client.source || 'Non spécifiée'}
            </Descriptions.Item>
            
            <Descriptions.Item label={<span><TagOutlined /> Points</span>}>
              {getPointsDisplay()}
            </Descriptions.Item>
            
            <Descriptions.Item label="Liste noire" span={2}>
              {getBlacklistStatus()}
            </Descriptions.Item>
            
            <Descriptions.Item label={<span><ClockCircleOutlined /> Créé le</span>}>
              {formatDate(client.created_at)}
            </Descriptions.Item>
            
            <Descriptions.Item label="Dernière modification">
              {formatDate(client.updated_at)}
            </Descriptions.Item>
          </Descriptions>
          
          {client.customer_addresses && client.customer_addresses.length > 0 && (
            <>
              <Divider orientation="left">Adresses</Divider>
              {client.customer_addresses.map((address, index) => (
                <Descriptions key={address.id || index} bordered size="small" style={{ marginBottom: '15px' }}>
                  <Descriptions.Item label="Titre" span={3}>
                    {address.title || `Adresse ${index + 1}`}
                  </Descriptions.Item>
                  <Descriptions.Item label="Adresse complète" span={3}>
                    {address.address || 'Non spécifiée'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Ville">
                    {address.city || 'Non spécifiée'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Pays">
                    {address.country || 'Non spécifié'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Par défaut">
                    {address.is_default ? <Tag color="blue">Adresse par défaut</Tag> : 'Non'}
                  </Descriptions.Item>
                </Descriptions>
              ))}
            </>
          )}
          
          <Divider orientation="left">Autres informations</Divider>
          <Descriptions bordered size="small">
            <Descriptions.Item label="Notes" span={3}>
              {client.notes || 'Aucune note'}
            </Descriptions.Item>
          </Descriptions>
        </div>
      ) : (
        <div style={{ textAlign: 'center', padding: '20px' }}>
          Aucune information client trouvée.
        </div>
      )}
    </Modal>
  );
};

export default ClientDetails; 